import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './About.css'
import aboutImage from  '../../Assets/Image/banner/camels.jpg'

import styled from 'styled-components';

const About = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <StyledAboutSection ref={ref}>
      <Container>
        <Row className="align-items-center">
          <Col lg={6}>
            <motion.div 
              className="about-image-wrapper"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, scale: 1 }
              }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="image-grid">
                <div className="main-image">
                  <img src={aboutImage} alt="Afar Region" />
                  <div className="image-overlay"></div>
                </div>
                <div className="accent-shapes">
                  <div className="shape shape-1"></div>
                  <div className="shape shape-2"></div>
                </div>
                <div className="experience-badge">
                  <span className="number">15+</span>
                  <span className="text">Years of Excellence</span>
                </div>
              </div>
            </motion.div>
          </Col>

          <Col lg={6}>
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, y: 50 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, y: 0 }
              }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="section-header">
                <motion.div 
                  className="tag-line"
                  initial={{ width: 0 }}
                  animate={controls}
                  variants={{
                    visible: { width: "100%" }
                  }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  <span>Discover Afar</span>
                </motion.div>
                <h3> About Afar Tourizm Bureau</h3>
              </div>

              <div className="content-blocks">
                <motion.div 
                  className="info-block"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="info-icon">
                    <span>🌋</span>
                  </div>
                  <div className="info-content">
                    <h4>Our Vision</h4>
                    <p>Witness the dramatic beauty of active volcanoes and unique geological formations.</p>
                  </div>
                </motion.div>

                <motion.div 
                  className="info-block"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="info-icon">
                    <span>🏺</span>
                  </div>
                  <div className="info-content">
                    <h4>Our Mission</h4>
                    <p>Immerse yourself in the ancient traditions and vibrant culture of the Afar people.</p>
                  </div>
                </motion.div>
              </div>

              <div className="stats-container">
                {[
                  { number: "20+", label: "Tourist Sites" },
                  { number: "10000+", label: "Happy Visitors" },
                  { number: "100%", label: "Satisfaction" }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    className="stat-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={controls}
                    variants={{
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: { delay: 0.8 + (index * 0.1) }
                      }
                    }}
                  >
                    <span className="stat-number">{stat.number}</span>
                    <span className="stat-label">{stat.label}</span>
                  </motion.div>
                ))}
              </div>

              <motion.button 
                className="explore-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore More
                <span className="button-icon">→</span>
              </motion.button>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </StyledAboutSection>
  );
};

const StyledAboutSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 0% 0%, rgba(45, 123, 168, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  .about-image-wrapper {
    position: relative;
    z-index: 2;
  }

  .image-grid {
    position: relative;
    padding: 2rem;
  }

  .main-image {
    position: relative;
    border-radius: 30px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);

    img {
      width: 100%;
      height: 500px;
      object-fit: cover;
      transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover img {
      transform: scale(1.1);
    }
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      45deg,
      rgba(45, 123, 168, 0.2),
      rgba(52, 73, 94, 0.2)
    );
    z-index: 1;
  }

  .accent-shapes {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
    
    .shape {
      position: absolute;
      border-radius: 50%;
      
      &-1 {
        width: 200px;
        height: 200px;
        background: rgba(45, 123, 168, 0.1);
        top: -50px;
        right: -50px;
      }
      
      &-2 {
        width: 150px;
        height: 150px;
        background: rgba(52, 73, 94, 0.1);
        bottom: -30px;
        left: -30px;
      }
    }
  }

  .experience-badge {
    position: absolute;
    bottom: 40px;
    right: 0;
    background: white;
    padding: 20px;
    border-radius: 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;

    .number {
      display: block;
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d7ba8;
      line-height: 1;
    }

    .text {
      font-size: 0.9rem;
      color: #666;
    }
  }

  .about-content {
    padding-left: 3rem;

    .section-header {
      margin-bottom: 3rem;

      .tag-line {
        display: inline-block;
        background: linear-gradient(45deg, #2d7ba8, #34495e);
        padding: 8px 20px;
        border-radius: 50px;
        margin-bottom: 1.5rem;
        
        span {
          color: white;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
      }

      h2 {
        font-size: 3.5rem;
        font-weight: 700;
        line-height: 1.2;
        background: linear-gradient(45deg, #1e293b, #334155);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    .content-blocks {
      display: grid;
      gap: 2rem;
      margin-bottom: 3rem;

      .info-block {
        display: flex;
        align-items: flex-start;
        gap: 1.5rem;
        padding: 2rem;
        background: white;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease;

        .info-icon {
          font-size: 2.5rem;
          line-height: 1;
        }

        .info-content {
          h4 {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: #1e293b;
          }

          p {
            font-size: 1rem;
            line-height: 1.6;
            color: #64748b;
            margin: 0;
          }
        }
      }
    }

    .stats-container {
      display: flex;
      justify-content: space-between;
      margin-bottom: 3rem;
      
      .stat-item {
        text-align: center;
        
        .stat-number {
          display: block;
          font-size: 2.5rem;
          font-weight: 700;
          color: #2d7ba8;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          font-size: 0.9rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      }
    }

    .explore-button {
      display: inline-flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 2.5rem;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      color: white;
      border: none;
      border-radius: 50px;
      font-size: 1.1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      .button-icon {
        transition: transform 0.3s ease;
      }

      &:hover .button-icon {
        transform: translateX(5px);
      }
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }

  @media (max-width: 992px) {
    padding: 80px 0;

    .about-content {
      padding-left: 0;
      margin-top: 3rem;
      text-align: center;

      .section-header h2 {
        font-size: 2.5rem;
      }

      .content-blocks {
        grid-template-columns: 1fr;
      }

      .info-block {
        flex-direction: column;
        align-items: center;
        text-align: center;
      }

      .stats-container {
        flex-direction: column;
        gap: 2rem;
      }
    }
  }
`;

export default About;